/* Devoirs d'un enfant (espace parent/tuteur) : liste avec actions directes — */
/* voir le devoir (PDF in-app) et rendre des photos (modale glisser-déposer). */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import PdfPanel from '../components/pdf-panel';
import SubmitDevoirModal from '../components/submit-devoir-modal';
import SubmittedAssets from '../components/submitted-assets';
import StudentCorrection from '../components/student-correction';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import StudentExamResults from '../components/student-exam-results';
import StudentChatCard from '../components/student-chat-card';
import StudentFluenceCard from '../components/student-fluence-card';
import { api } from '../lib/api';

interface Child {
  classroom_student_id: string;
  first_name: string;
  last_name: string;
}

interface Item {
  assignment_id: string;
  title: string;
  format: string;
  due_at: string | null;
  submission_status: string;
}

const STATUS_LABEL: Record<string, string> = {
  not_started: 'À faire',
  draft: 'En cours',
  submitted: 'Rendu',
};
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

const pageClass = "min-h-dvh bg-background flex flex-col";
const wrapClass = "max-w-[880px] mx-auto px-4 py-6 flex-1 w-full";
const rowClass = "flex items-center gap-3 px-4 py-[14px] bg-card border-[1.5px] border-border rounded-sm flex-wrap";

export default function ResponsableChild() {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const [pdf, setPdf] = useState<{ url: string; title: string } | null>(null);
  const [submitFor, setSubmitFor] = useState<Item | null>(null);

  const reload = useCallback(() => {
    if (!studentId) return;
    setLoading(true);
    api.parentOrTutor.student.assignments
      .query({ studentId })
      .then((r) => setItems(r as Item[]))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => reload(), [reload]);

  useEffect(() => {
    api.parentOrTutor.myChildren
      .query()
      .then((c) => setChildren(c as Child[]))
      .catch(() => {
        /* header enrichi optionnel */
      });
  }, []);

  const current = children.find((c) => c.classroom_student_id === studentId);
  const pdfKey = studentId ? `responsable-pdf:${studentId}` : '';

  const openPdf = useCallback(
    async (assignmentId: string, title: string) => {
      if (!studentId) return;
      setPdfBusyId(assignmentId);
      setError(null);
      try {
        const { signed_url } = await api.parentOrTutor.student.assignmentPdf.mutate({ studentId, assignmentId });
        setPdf({ url: signed_url, title });
        localStorage.setItem(pdfKey, JSON.stringify({ assignmentId, title }));
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setPdfBusyId(null);
      }
    },
    [studentId, pdfKey],
  );

  const closePdf = () => {
    setPdf(null);
    if (pdfKey) localStorage.removeItem(pdfKey);
  };

  /* Réouvre le panneau PDF laissé ouvert avant un refresh (le PDF est en cache serveur). */
  useEffect(() => {
    if (!pdfKey) return;
    const saved = localStorage.getItem(pdfKey);
    if (!saved) return;
    try {
      const { assignmentId, title } = JSON.parse(saved) as { assignmentId: string; title: string };
      if (assignmentId && title) void openPdf(assignmentId, title);
    } catch {
      localStorage.removeItem(pdfKey);
    }
  }, [pdfKey, openPdf]);

  return (
    <div className={pageClass}>
      <HeaderContent width="md" showBurger backTo="/tutor" backLabel="Mes enfants">
        <h1 className={HEADER_TITLE_CLASS}>
          {current ? `Devoirs — ${current.first_name} ${current.last_name}` : 'Devoirs'}
        </h1>
      </HeaderContent>
      <div className={wrapClass}>

        {children.length > 1 && (
          <div className="flex gap-[10px] flex-wrap justify-start mb-3">
            {children.map((c) => {
              const active = c.classroom_student_id === studentId;
              return (
                <Button
                  key={c.classroom_student_id}
                  variant={active ? 'default' : 'secondary'}
                  onClick={() => navigate(`/tutor/enfant/${c.classroom_student_id}`)}
                >
                  {c.first_name}
                </Button>
              );
            })}
          </div>
        )}

        {error && <Alert>{error}</Alert>}

        {studentId && <StudentChatCard studentId={studentId} />}

        {studentId && <StudentExamResults studentId={studentId} />}

        {studentId && <StudentFluenceCard studentId={studentId} />}

        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-12 px-4 text-muted-foreground">
            <div className="text-[40px]">📝</div>
            <p className="font-extrabold text-foreground mt-2 mb-1">Aucun devoir pour le moment</p>
          </div>
        ) : (
          <div className="flex flex-col gap-[10px]">
            {items.map((it) => (
              <div key={it.assignment_id} className={rowClass}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-foreground">{it.title}</div>
                  <div className="text-[13px] text-muted-foreground mt-0.5">
                    {it.due_at ? `À rendre le ${fmtDate(it.due_at)}` : 'Sans date limite'}
                  </div>
                </div>
                <Badge>{STATUS_LABEL[it.submission_status] ?? it.submission_status}</Badge>
                {it.format === 'interactive' ? (
                  <div className="flex gap-[10px] flex-wrap justify-start w-full mt-2">
                    <Button onClick={() => navigate(`/tutor/enfant/${studentId}/parcours/${it.assignment_id}`)}>
                      {it.submission_status === 'submitted' ? 'Revoir le parcours' : it.submission_status === 'draft' ? 'Continuer le parcours' : '✨ Commencer le parcours'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-[10px] flex-wrap justify-start w-full mt-2">
                      <Button
                        variant="secondary"
                        onClick={() => void openPdf(it.assignment_id, it.title)}
                        disabled={pdfBusyId === it.assignment_id}
                      >
                        {pdfBusyId === it.assignment_id ? 'Préparation…' : '📄 Voir le devoir'}
                      </Button>
                      <Button onClick={() => setSubmitFor(it)}>
                        {it.submission_status === 'submitted' ? 'Renvoyer' : 'Rendre'}
                      </Button>
                    </div>
                    {it.submission_status === 'submitted' && studentId && (
                      <>
                        <SubmittedAssets studentId={studentId} assignmentId={it.assignment_id} />
                        <StudentCorrection studentId={studentId} assignmentId={it.assignment_id} />
                      </>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {submitFor && studentId && (
        <SubmitDevoirModal
          studentId={studentId}
          assignmentId={submitFor.assignment_id}
          title={submitFor.title}
          onClose={() => setSubmitFor(null)}
          onDone={() => {
            setSubmitFor(null);
            reload();
          }}
        />
      )}

      {pdf && <PdfPanel url={pdf.url} title={pdf.title} onClose={closePdf} />}
    </div>
  );
}
